import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BasicTemplate from 'components/Toast/BasicTemplate';
import { post, patch } from 'services/permissions/permissionSetupServices';

const newItem = { id: 0, code: '', name: '', description: '' };

const PermissionSetupModal = ({ data, isShow, handleClose, handleSubmit }) => {
  const [formData, setFormData] = useState(newItem);
  const [validated, setValidated] = useState(false);
  const [isWaitingSave, setIsWaitingSave] = useState(false);

  useEffect(() => {
    setFormData(data && data.id > 0 ? data : newItem);
    setValidated(false);
  }, [data, isShow]);

  const handleChange = (e, name) => {
    const { value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const onSubmit = async event => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }
    setValidated(true);
    setIsWaitingSave(true);

    const result =
      formData.id > 0
        ? await patch(formData, formData.id)
        : await post(formData);

    const message =
      result.message ??
      'There was a problem saving the data. Please check your entires and try again.';
    result.success
      ? toast.success(
          <BasicTemplate
            success={result.success}
            text="Permission Setup successfully saved!"
          />
        )
      : toast.error(<BasicTemplate success={result.success} text={message} />);
    setIsWaitingSave(false);

    if (result.success) {
      handleSubmit();
    }
  };

  return (
    <Modal
      show={isShow}
      onHide={handleClose}
      contentClassName="border"
      centered
    >
      <Form noValidate validated={validated} onSubmit={onSubmit}>
        <Modal.Header closeButton className="bg-light px-card border-bottom-0">
          <Modal.Title as="h5">
            {formData?.id > 0
              ? 'Edit ' + formData.code
              : 'Create Permission Setup'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <Row className="mb-3">
            <Form.Group as={Col} md="12" className="mb-3" controlId="code">
              <Form.Label className="required">Code</Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="Code"
                value={formData?.code ?? ''}
                onChange={e => handleChange(e, 'code')}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a Code.
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="12" className="mb-3" controlId="name">
              <Form.Label className="required">Name</Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="Name"
                value={formData?.name ?? ''}
                onChange={e => handleChange(e, 'name')}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a Name.
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="12" controlId="description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Description"
                value={formData?.description ?? ''}
                onChange={e => handleChange(e, 'description')}
              />
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            disabled={isWaitingSave}
            className="px-4 mx-0"
          >
            {!isWaitingSave ? (
              'Save'
            ) : (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span> ...Loading</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

PermissionSetupModal.propTypes = {
  data: PropTypes.object,
  isShow: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func
};

export default PermissionSetupModal;
